import { Package, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CartItem, ItemVariant, POSItem } from '@/types';
import { getItemPrice, getLowestVariantPrice } from '@/utils/posItemPricing';
import { resolveFileUrl } from '@/utils/fileUrl';
import { useFavoriteIds, useFavoritesStore } from '@/store/favoritesStore';
import { Star } from 'lucide-react';
import { previewItemPromo, type PromotionCampaign } from '@/utils/promotions';
import { PosPromoPrice, PosSaleBadge } from '@/components/pos/PosSaleBadge';

function getCartQtyForItem(items: CartItem[], itemName: string, variant?: string) {
  return items.reduce((sum, line) => {
    if (line.item_type !== 'Product' || line.item !== itemName) return sum;
    if (variant !== undefined && (line.variant || '') !== variant) return sum;
    return sum + line.quantity;
  }, 0);
}

function stockBadgeClass(qty: number) {
  if (qty <= 0) return 'bg-red-50 text-red-700';
  if (qty <= 5) return 'bg-amber-50 text-amber-700';
  return 'bg-emerald-50 text-emerald-700';
}

interface PosCompactListViewProps {
  items: POSItem[];
  cartItems: CartItem[];
  allowOos: boolean;
  customerType: string;
  promotions?: PromotionCampaign[];
  userId?: string;
  onAddItem: (item: POSItem, variant?: ItemVariant) => void;
  onOpenVariantModal: (itemName: string) => void;
  onToggleFavorite?: (itemName: string) => void;
}

export default function PosCompactListView({
  items,
  cartItems,
  allowOos,
  customerType,
  promotions = [],
  userId,
  onAddItem,
  onOpenVariantModal,
  onToggleFavorite,
}: PosCompactListViewProps) {
  const { t } = useTranslation();
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  const favoriteIds = useFavoriteIds(userId || '');

  const handleToggleFavorite = (itemName: string) => {
    if (onToggleFavorite) onToggleFavorite(itemName);
    else if (userId) toggleFavorite(userId, itemName);
  };

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => {
        const hasVariants = item.has_variants && (item.variants?.length ?? 0) > 0;
        const outOfStock =
          item.item_type === 'Product' && !allowOos && !item.has_variants && (item.stock_quantity ?? 0) <= 0;
        const disabled = outOfStock;
        const displayPrice = hasVariants
          ? getLowestVariantPrice(item, customerType)
          : getItemPrice(item, customerType);
        const promo = hasVariants ? null : previewItemPromo(item, customerType, promotions);

        const handleClick = () => {
          if (disabled) return;
          if (hasVariants) onOpenVariantModal(item.name);
          else onAddItem(item);
        };

        const inCart = getCartQtyForItem(cartItems, item.name);

        return (
          <div
            key={item.name}
            className={`card flex items-center gap-3 p-3 min-h-[56px] ${disabled ? 'opacity-50' : ''}`}
          >
            <button
              type="button"
              onClick={handleClick}
              disabled={disabled}
              className="flex flex-1 items-center gap-3 text-left min-h-[48px]"
            >
              <div className="w-12 h-12 rounded-lg bg-gray-100 shrink-0 overflow-hidden flex items-center justify-center">
                {item.image ? (
                  <img src={resolveFileUrl(item.image)} alt="" className="w-full h-full object-cover" />
                ) : item.item_type === 'Product' ? (
                  <Package className="w-5 h-5 text-blue-400" />
                ) : (
                  <Wrench className="w-5 h-5 text-purple-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                  <span className="truncate">{item.item_name}</span>
                  <PosSaleBadge preview={promo} />
                </div>
                <div className="text-sm text-gray-500">{item.item_code || item.name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-semibold text-primary-700">
                  <PosPromoPrice price={displayPrice} preview={promo} />
                </div>
                {item.item_type === 'Product' && !item.has_variants && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${stockBadgeClass(item.stock_quantity ?? 0)}`}>
                    {item.stock_quantity ?? 0} {t('pos.inStock', { defaultValue: 'in stock' })}
                  </span>
                )}
                {inCart > 0 && (
                  <span className="text-xs text-primary-600 font-medium block">{inCart} in cart</span>
                )}
              </div>
            </button>
            {userId && (
              <button
                type="button"
                onClick={() => handleToggleFavorite(item.name)}
                className="p-2 shrink-0 text-amber-500 hover:text-amber-600"
                aria-label="Toggle favorite"
              >
                <Star
                  className={`w-5 h-5 ${favoriteIds.includes(item.name) ? 'fill-amber-400 text-amber-500' : 'text-amber-500'}`}
                />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
